import { useEffect, useRef, useState } from 'react'
import { AlertTriangle, Check, Loader2, Route as RouteIcon } from 'lucide-react'

import { useRoadbook } from '../../data/roadbookContext'
import { hours, num, timeAgo } from '../../lib/format'
import {
  isCoarseRoute,
  isDrive,
  routeProposal,
  routeState,
  stageRouteFrom,
  viaCoords,
} from '../../lib/route'
import { fahrzeug } from '../../lib/vehicle'
import type { Stage } from '../../lib/types'
import { useFetchRoute } from './routeApi'
import { routeErrorText } from './stageRouting'
import { ViaEditor } from './ViaEditor'

/**
 * Der Patch, der die Zwischenpunkte wieder herausnimmt.
 *
 * undefined und nicht die leere Liste: ein Dokument mit einem Feld, das da ist
 * und nichts enthält, sieht nach dem Abgleich anders aus als eines ohne das
 * Feld - und ist doch dasselbe. Beim Serialisieren fällt der Schlüssel weg,
 * und eine Etappe ohne Zwischenpunkte sieht danach genauso aus wie vor deren
 * Einführung.
 */
const ohneVia: Partial<Stage> = { via: undefined }

/**
 * Die Strecke einer Etappe berechnen und die Werte daraus übernehmen.
 *
 * Berechnet wird auf Knopfdruck und nur dann. Die Strecke bleibt danach in der
 * Etappe liegen, denn die App wird im Funkloch benutzt: was hier einmal geholt
 * wurde, ist unterwegs da, auch ohne Netz.
 *
 * Übernommen wird nichts von selbst. Die Fahrzeit des Dienstes gilt für einen
 * Personenwagen, unterwegs ist vielleicht ein beladener Bus - der Zuschlag
 * darauf ist eine Schätzung, und eine Schätzung, die sich unbemerkt in die
 * Planung schreibt, wäre eine Behauptung. Also stehen beide Zahlen da, samt der
 * Planung, wie sie gerade ist, und der Nutzer drückt.
 *
 * Wie hoch der Zuschlag ist, sagt das Fahrzeug der Reise (lib/vehicle.ts).
 * Hier stand ein festes Viertel, und damit plante auch ein Personenwagen wie
 * ein Camper: der hervorgehobene Knopf übernahm die Camperzeit, und der Text
 * daneben behauptete für jede Reise, der beladene T3 fahre 90 bis 100. Ist
 * kein Zuschlag hinterlegt, gibt es die Zeile nicht und der Knopf übernimmt
 * die Zeit des Dienstes.
 *
 * Womit gerechnet wird, steht mit im Block: die Zwischenpunkte stehen zwischen
 * den Zahlen und dem Knopf. Sie bestimmen, welchen Weg die Strecke nimmt, und
 * ihre Änderung ist einer der beiden Gründe, aus denen die Warnung oben
 * erscheint.
 */
export function StageRouteEditor({
  stage,
  onChange,
}: {
  stage: Stage
  onChange: (patch: Partial<Stage>) => void
}) {
  const { trip } = useRoadbook()
  const auto = fahrzeug(trip)
  const fetchRoute = useFetchRoute()
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const abbruch = useRef<AbortController | null>(null)

  // Wer die Etappe zuklappt, während die Anfrage laeuft, hat es sich anders
  // überlegt. Ohne den Abbruch schriebe die Antwort danach noch in eine
  // Etappe, die niemand mehr ansieht.
  useEffect(
    () => () => {
      abbruch.current?.abort()
    },
    [],
  )

  const zustand = routeState(stage)
  const route = zustand === 'current' ? stage.route : undefined
  const vorschlag = route ? routeProposal(route, auto.surcharge) : null
  const fahrt = isDrive(stage)

  const berechnen = async () => {
    setFehler(null)
    setLaeuft(true)
    const controller = new AbortController()
    abbruch.current = controller
    try {
      const antwort = await fetchRoute(stage.c1, viaCoords(stage), stage.c2, controller.signal)
      const neu = stageRouteFrom(antwort, stage, Date.now())
      if (!neu) {
        setFehler('Die Antwort des Dienstes enthält keine brauchbare Strecke.')
        return
      }
      onChange({ route: neu })
    } catch (error) {
      if (controller.signal.aborted) return
      setFehler(routeErrorText(error))
    } finally {
      if (abbruch.current === controller) abbruch.current = null
      setLaeuft(false)
    }
  }

  return (
    <section className="inhalt-block strecke-block kein-druck">
      <h5 className="block-titel">
        <RouteIcon size={13} /> Strecke
      </h5>

      {/*
        Der Standtag, und ausdrücklich nur er: Start und Ziel am selben Ort und
        nichts dazwischen. Der Satz stand hier für jede Etappe, deren Start und
        Ziel zusammenfielen - und damit auch für die Tagesrunde über Zofingen,
        Olten und Aarau nach Hause, die achtzig Kilometer lang ist. Weil er
        obendrein den Block darunter verbarg, liess sich der Widerspruch nicht
        einmal auflösen: die Zwischenziele, die aus dem Standtag eine Runde
        machen, waren genau dort einzutragen, wo nichts mehr stand.
      */}
      {!fahrt && (
        <p className="leer">
          Start und Ziel sind derselbe Ort, und dazwischen liegt nichts: ein Standtag, für den es
          keine Strecke zu berechnen gibt. Wer eine Runde fährt, trägt unten die Zwischenziele ein.
        </p>
      )}

      {fahrt && zustand === 'none' && (
        <p className="leer">
          Noch nicht berechnet. Die Karte zeichnet für diese Etappe die Luftlinie.
        </p>
      )}

      {fahrt && zustand === 'stale' && (
        <p className="karte-warnung">
          <AlertTriangle size={13} /> Koordinaten oder Zwischenpunkte wurden geändert, nachdem die
          Strecke berechnet wurde. Sie führt woandershin und wird deshalb nicht mehr gezeichnet —
          bis sie neu berechnet ist, zeigt die Karte wieder die Luftlinie.
        </p>
      )}

      {/*
        Die Zahlen stimmen, nur die Linie ist grob: sie stammt aus der Zeit, in
        der in gleichmässigen Abständen ausgedünnt wurde, und schneidet an
        Pässen die Kehren ab. Kein Warnton deswegen - es geht nichts verloren,
        wenn niemand drückt.
      */}
      {fahrt && zustand === 'current' && isCoarseRoute(stage) && (
        <p className="leer">
          Diese Strecke wurde mit dem früheren Verfahren gezeichnet und schneidet Kurven ab. Neu
          berechnen setzt die Stützpunkte dorthin, wo die Strasse ihre Richtung ändert.
        </p>
      )}

      {route && vorschlag && (
        <>
          <dl className="strecke-werte">
            <div>
              <dt>Strecke laut Dienst</dt>
              <dd>{num(route.distanceKm)} km</dd>
            </div>
            <div>
              <dt>Fahrzeit Personenwagen</dt>
              <dd>{hours(route.durationH)} h</dd>
            </div>
            {/*
              Nur wenn es einen gibt. Ohne Zuschlag wäre das eine zweite Zeile
              mit derselben Zahl - und eine Überschrift, die einen Aufschlag
              behauptet, den niemand eingetragen hat.
            */}
            {vorschlag.surcharge > 0 && (
              <div>
                <dt>
                  {auto.name || 'Fahrzeug'}, {Math.round(vorschlag.surcharge * 100)} % Zuschlag
                </dt>
                <dd>{hours(vorschlag.vehicleH)} h</dd>
              </div>
            )}
            <div>
              <dt>Planung jetzt</dt>
              <dd>
                {num(stage.km)} km · {hours(stage.h)} h
              </dd>
            </div>
          </dl>

          {/*
            Hier stand für jede Reise, der beladene T3 fahre 90 bis 100. Was
            gilt, hängt am Fahrzeug: mit Zuschlag wird gesagt, dass er eine
            Schätzung ist und woher er kommt, ohne Zuschlag wird gar nichts
            behauptet.
          */}
          <p className="abschluss">
            Berechnet {timeAgo(route.computedAt)}. Die Fahrzeit des Dienstes gilt für einen
            gewöhnlichen Personenwagen.{' '}
            {vorschlag.surcharge > 0
              ? `Die ${String(Math.round(vorschlag.surcharge * 100))} % Zuschlag kommen aus dem Fahrzeug im Reiter Daten — eine Schätzung, kein Messwert.`
              : 'Für dieses Fahrzeug ist kein Zuschlag hinterlegt; im Reiter Daten lässt sich einer eintragen.'}
          </p>

          <div className="knopfreihe">
            <button
              type="button"
              className="knopf"
              disabled={stage.km === vorschlag.km && stage.h === vorschlag.vehicleH}
              onClick={() => onChange({ km: vorschlag.km, h: vorschlag.vehicleH })}
            >
              <Check size={14} /> {num(vorschlag.km)} km und {hours(vorschlag.vehicleH)} h
              übernehmen
            </button>
            <button
              type="button"
              className="knopf still"
              disabled={stage.km === vorschlag.km}
              onClick={() => onChange({ km: vorschlag.km })}
            >
              Nur Kilometer
            </button>
            {/*
              Ohne Zuschlag ist das derselbe Knopf wie der erste, nur ohne die
              Kilometer - und die Fahrzeit steht dann schon oben.
            */}
            {vorschlag.surcharge > 0 && (
              <button
                type="button"
                className="knopf still"
                disabled={stage.h === vorschlag.carH}
                onClick={() => onChange({ h: vorschlag.carH })}
              >
                Fahrzeit ohne Zuschlag ({hours(vorschlag.carH)} h)
              </button>
            )}
          </div>
        </>
      )}

      {/*
        Die Zwischenpunkte stehen unter den Zahlen und über dem Knopf: sie
        bestimmen, was der nächste Druck holt, und ihre Änderung ist der Grund,
        aus dem die Warnung darüber erscheint.

        Ohne Bedingung, und das ist der Kern der Sache: sie sind das Einzige,
        was aus einem Standtag eine Runde macht. Hinter „fahrt“ versteckt
        blieben sie genau der Etappe verborgen, die sie braucht - man hätte das
        Ziel verschieben, die Punkte eintragen und das Ziel zurückschieben
        müssen. Ein neuer Punkt sitzt zunächst auf dem Ziel und ändert deshalb
        an einem Standtag nichts, solange niemand ihn versetzt.
      */}
      <ViaEditor
        werte={stage.via ?? []}
        ziel={stage.c2}
        onChange={(via) => onChange(via.length > 0 ? { via } : ohneVia)}
      />

      {fahrt && (
        <div className="knopfreihe">
          <button
            type="button"
            className={zustand === 'current' ? 'knopf still' : 'knopf'}
            disabled={laeuft}
            onClick={() => void berechnen()}
          >
            {laeuft ? <Loader2 size={14} className="dreht" /> : <RouteIcon size={14} />}
            {laeuft
              ? 'Strecke wird berechnet …'
              : zustand === 'none'
                ? 'Strecke berechnen'
                : 'Strecke neu berechnen'}
          </button>
        </div>
      )}

      {fehler && <p className="karte-warnung">{fehler}</p>}
    </section>
  )
}
